import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Map, BookOpen, FileText, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import Logo from './Logo';
import { trackCtaClick } from '../lib/analytics';

const PRIMARY_NAV = [
  { label: 'How It Works', href: '#how-it-works', type: 'anchor' },
  { label: 'Savings Calculator', href: '#calculator', type: 'anchor' },
  { label: 'Comparison', href: '#comparison', type: 'anchor' },
  { label: 'Pricing', href: '#pricing', type: 'anchor' },
];

const RESOURCES_MENU = [
  {
    label: 'Zone Map',
    description: 'Ground transit times from Weston, FL',
    href: '/zones',
    icon: Map,
  },
  {
    label: 'Blog',
    description: 'Operator guides for D2C founders',
    href: '/blog',
    icon: BookOpen,
  },
  {
    label: 'Integrations',
    description: 'Shopify, TikTok Shop, Amazon & more',
    href: '/integrations',
    icon: FileText,
  },
  {
    label: 'FAQ',
    description: 'Common questions, answered',
    href: '/faq',
    icon: HelpCircle,
  },
];

const CONTACT_MENU = [
  { label: 'About Us', href: '/about' },
  { label: 'Book a Call', href: '/book' },
  { label: 'Get a Quote', href: '/#contact' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  // Resolve anchor href correctly when not on home
  const resolveAnchor = (href) => {
    if (!href.startsWith('#')) return href;
    return location.pathname === '/' ? href : `/${href}`;
  };

  const handleNavClick = (e, href) => {
    if (!href.startsWith('#')) return;
    if (location.pathname !== '/') {
      e.preventDefault();
      navigate(`/${href}`);
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full backdrop-blur-md transition-colors duration-200 ${
        scrolled ? 'bg-white/90 shadow-sm border-b border-slate-100' : 'bg-white/70'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {PRIMARY_NAV.map((item) => (
              <a
                key={item.label}
                href={resolveAnchor(item.href)}
                onClick={(e) => handleNavClick(e, item.href)}
                className="text-[15px] font-medium text-slate-700 hover:text-indigo-600 transition-colors duration-150"
              >
                {item.label}
              </a>
            ))}

            {/* Resources dropdown */}
            <div
              className="relative"
              onMouseEnter={() => {
                cancelClose();
                setOpenMenu('resources');
              }}
              onMouseLeave={scheduleClose}
            >
              <button
                type="button"
                className="flex items-center gap-1 text-[15px] font-medium text-slate-700 hover:text-indigo-600 transition-colors duration-150"
              >
                Resources
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${
                    openMenu === 'resources' ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openMenu === 'resources' && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[340px]">
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-900/5 p-2">
                    {RESOURCES_MENU.map((item) => {
                      const Icon = item.icon;
                      const content = (
                        <div className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-indigo-50 transition-colors group">
                          <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-50 group-hover:bg-white flex items-center justify-center transition-colors">
                            <Icon className="w-4.5 h-4.5 text-indigo-600" />
                          </span>
                          <div>
                            <p className="text-sm font-bold text-slate-900">{item.label}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                          </div>
                        </div>
                      );
                      return item.href.startsWith('/') ? (
                        <Link key={item.label} to={item.href}>
                          {content}
                        </Link>
                      ) : (
                        <a key={item.label} href={item.href}>
                          {content}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Contact dropdown */}
            <div
              className="relative"
              onMouseEnter={() => {
                cancelClose();
                setOpenMenu('contact');
              }}
              onMouseLeave={scheduleClose}
            >
              <button
                type="button"
                className="flex items-center gap-1 text-[15px] font-medium text-slate-700 hover:text-indigo-600 transition-colors duration-150"
              >
                Contact
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${
                    openMenu === 'contact' ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openMenu === 'contact' && (
                <div className="absolute right-0 top-full pt-3 w-[220px]">
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-900/5 p-2">
                    {CONTACT_MENU.map((item) => {
                      const linkClass =
                        'block px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors';
                      // Routed page (e.g. /about)
                      if (item.href.startsWith('/') && !item.href.includes('#')) {
                        return (
                          <Link key={item.label} to={item.href} className={linkClass}>
                            {item.label}
                          </Link>
                        );
                      }
                      // Cross-page anchor like /#contact -- resolve correctly
                      let href = item.href;
                      if (item.href.startsWith('/#')) {
                        const hashOnly = item.href.slice(1); // -> '#contact'
                        href = location.pathname === '/' ? hashOnly : item.href;
                      }
                      return (
                        <a key={item.label} href={href} className={linkClass}>
                          {item.label}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Button
              asChild
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-5 h-10 font-semibold shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Link
                to="/book"
                onClick={() => trackCtaClick({ label: 'Book a Call!', location: 'header_desktop', destination: '/book' })}
                data-testid="header-book-call-desktop"
              >
                Book a Call!
              </Link>
            </Button>
          </div>

          <button
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-100 bg-white">
          <div className="px-4 py-4 space-y-1">
            {PRIMARY_NAV.map((item) => (
              <a
                key={item.label}
                href={resolveAnchor(item.href)}
                onClick={(e) => {
                  handleNavClick(e, item.href);
                  setMobileOpen(false);
                }}
                className="block py-2.5 text-base font-medium text-slate-700 hover:text-indigo-600"
              >
                {item.label}
              </a>
            ))}
            <div className="pt-2">
              <p className="px-1 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                Resources
              </p>
              {RESOURCES_MENU.map((item) =>
                item.href.startsWith('/') ? (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="block py-2 pl-3 text-base font-medium text-slate-700 hover:text-indigo-600"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="block py-2 pl-3 text-base font-medium text-slate-700 hover:text-indigo-600"
                  >
                    {item.label}
                  </a>
                )
              )}
            </div>
            <div className="pt-3 flex flex-col gap-2">
              <Button
                asChild
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-10"
              >
                <Link
                  to="/book"
                  onClick={() => {
                    trackCtaClick({ label: 'Book a Call!', location: 'header_mobile', destination: '/book' });
                    setMobileOpen(false);
                  }}
                  data-testid="header-book-call-mobile"
                >
                  Book a Call!
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
